/**
 * usePageMeta — lightweight head management for Vue 3 SPA.
 *
 * Sets document.title and key <meta> tags reactively.
 * No external dependency needed — works with Googlebot (JS-capable crawler).
 *
 * Usage:
 *   usePageMeta({
 *     title:       'Page Title | 11+ Training',
 *     description: 'Short description under ~155 chars.',
 *     canonical:   'https://11plustraining.co.uk/some-path',
 *   })
 */

const SITE_NAME = 'Maths Eleven Plus';
const BASE_URL  = 'https://mathselevenplus.co.uk';

function _setMeta(name, content, attr = 'name') {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function _setLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function usePageMeta({ title, description, canonical, ogImage } = {}) {
  const fullTitle = title ?? SITE_NAME;
  const url       = canonical ?? (BASE_URL + window.location.pathname);
  const image     = ogImage ?? `${BASE_URL}/og-default.png`;

  // Title
  document.title = fullTitle;

  // Core
  _setMeta('description',  description);
  _setMeta('robots',       'index, follow');

  // Open Graph
  _setMeta('og:type',        'website',     'property');
  _setMeta('og:site_name',   SITE_NAME,     'property');
  _setMeta('og:title',       fullTitle,     'property');
  _setMeta('og:description', description,   'property');
  _setMeta('og:url',         url,           'property');
  _setMeta('og:image',       image,         'property');

  // Twitter Card
  _setMeta('twitter:card',        'summary_large_image');
  _setMeta('twitter:title',       fullTitle);
  _setMeta('twitter:description', description);
  _setMeta('twitter:image',       image);

  // Canonical
  _setLink('canonical', url);
}
